// Generic question texts (used by most states)
const GENERIC_TEXTS = {
    NICOTINE: "In the last 12 months, have you used nicotine in any form?",
    MEDICAL_CONDITIONS:
        "In the past three years, have you received any treatment for OR been diagnosed by a doctor as having heart trouble, cancer, stroke, lung disease, liver disease, kidney disease, AIDS, lupus, ALS, schizophrenia or dementia?",
    DIAGNOSTIC_TESTING:
        "In the past 12 months, have you had diagnostic testing performed or recommended by a doctor for an undiagnosed condition?",
} as const;

const MEDICAL_CONDITIONS_VARIANTS = {
    NC: "In the past three years, have you received any treatment for OR been diagnosed by a doctor as having heart trouble, cancer, stroke, lung disease, liver disease, kidney disease, Acquired Immune Deficiency Syndrome, lupus, ALS, schizophrenia or dementia?",
    VT: "In the past three years, have you received any treatment for OR been diagnosed by a licensed physician as having heart trouble, cancer, stroke, lung disease, liver disease, kidney disease, AIDS, lupus, ALS, schizophrenia or dementia?",
    FL: "In the past three years, have you received any treatment for OR been diagnosed by a doctor as having heart trouble, cancer, stroke, lung disease, liver disease, kidney disease, lupus, ALS, schizophrenia or dementia?",
} as const;

const DIAGNOSTIC_TESTING_VARIANTS = {
    FL: "In the past 12 months, have you had diagnostic testing performed or recommended by a doctor for an undiagnosed condition OR been diagnosed by a doctor as having AIDS?",
    ME: "In the past 12 months, have you had diagnostic testing (excluding HIV) performed or recommended by a doctor for an undiagnosed condition?",
    NC: "In the past 12 months, have you had a health issue that has not been diagnosed by a doctor for which the doctor has performed or recommended that you undergo diagnostic testing?",
    OH: "In the past 12 months, have you had diagnostic testing (excluding HIV and AIDS) performed or recommended by a doctor for an undiagnosed condition?",
    VT: "In the past 12 months, have you had diagnostic testing performed or recommended by a licensed physician for an undiagnosed condition?",
    WA_MA: "In the past 12 months, have you had diagnostic testing (excluding Human Immunodeficiency Virus (AIDS virus)) performed or recommended by a doctor for an undiagnosed condition?",
} as const;

// Type for state-specific overrides
type StateSpecificText = {
    MEDICAL_CONDITIONS?: string;
    DIAGNOSTIC_TESTING?: string;
};

const STATE_SPECIFIC_TEXTS: Record<string, StateSpecificText> = {
    FL: {
        MEDICAL_CONDITIONS: MEDICAL_CONDITIONS_VARIANTS.FL,
        DIAGNOSTIC_TESTING: DIAGNOSTIC_TESTING_VARIANTS.FL,
    },
    ME: {
        DIAGNOSTIC_TESTING: DIAGNOSTIC_TESTING_VARIANTS.ME,
    },
    NC: {
        MEDICAL_CONDITIONS: MEDICAL_CONDITIONS_VARIANTS.NC,
        DIAGNOSTIC_TESTING: DIAGNOSTIC_TESTING_VARIANTS.NC,
    },
    OH: {
        DIAGNOSTIC_TESTING: DIAGNOSTIC_TESTING_VARIANTS.OH,
    },
    VT: {
        MEDICAL_CONDITIONS: MEDICAL_CONDITIONS_VARIANTS.VT,
        DIAGNOSTIC_TESTING: DIAGNOSTIC_TESTING_VARIANTS.VT,
    },
    WA: {
        DIAGNOSTIC_TESTING: DIAGNOSTIC_TESTING_VARIANTS.WA_MA,
    },
    MA: {
        DIAGNOSTIC_TESTING: DIAGNOSTIC_TESTING_VARIANTS.WA_MA,
    },
} as const;

export type StateCode = keyof typeof STATE_SPECIFIC_TEXTS | string;

export interface HealthQuestions {
    nicotine: string;
    medicalConditions: string;
    diagnosticTesting: string;
}

/**
 * Get all health questions for a specific state
 * @param state - Two-letter state code (e.g., "CA", "FL", "NC")
 * @returns Object containing all three question texts for the state
 */
export function getHealthQuestions(state: StateCode): HealthQuestions {
    const stateUpper = state?.toUpperCase();
    const stateSpecific = STATE_SPECIFIC_TEXTS[stateUpper];

    return {
        nicotine: GENERIC_TEXTS.NICOTINE,
        medicalConditions: stateSpecific?.MEDICAL_CONDITIONS || GENERIC_TEXTS.MEDICAL_CONDITIONS,
        diagnosticTesting: stateSpecific?.DIAGNOSTIC_TESTING || GENERIC_TEXTS.DIAGNOSTIC_TESTING,
    };
}

/**
 * Get the nicotine question text for a specific state
 * @param state - Two-letter state code
 * @returns The nicotine question text (currently same for all states)
 */
export function getNicotineQuestion(state: StateCode): string {
    // Currently all states use the same nicotine question
    return GENERIC_TEXTS.NICOTINE;
}

/**
 * Get the medical conditions question text for a specific state
 * @param state - Two-letter state code
 * @returns The medical conditions question text
 */
export function getMedicalConditionsQuestion(state: StateCode): string {
    const stateUpper = state?.toUpperCase();
    return STATE_SPECIFIC_TEXTS[stateUpper]?.MEDICAL_CONDITIONS || GENERIC_TEXTS.MEDICAL_CONDITIONS;
}

/**
 * Get the diagnostic testing question text for a specific state
 * @param state - Two-letter state code
 * @returns The diagnostic testing question text
 */
export function getDiagnosticTestingQuestion(state: StateCode): string {
    const stateUpper = state?.toUpperCase();
    return STATE_SPECIFIC_TEXTS[stateUpper]?.DIAGNOSTIC_TESTING || GENERIC_TEXTS.DIAGNOSTIC_TESTING;
}

export const STATEMENT_OF_HEALTH_QUESTIONS = {
    GENERIC: GENERIC_TEXTS,
    MEDICAL_CONDITIONS_VARIANTS,
    DIAGNOSTIC_TESTING_VARIANTS,
    STATE_SPECIFIC: STATE_SPECIFIC_TEXTS,
} as const;
